import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface ToastMessage {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

export interface ConfirmationDialog {
  id: string;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  type?: 'danger' | 'warning' | 'info';
}

export interface DeleteErrorDialog {
  id: string;
  title: string;
  message: string;
  errorReason: string;
  confirmText?: string;
  onConfirm: () => void;
  type?: 'danger' | 'warning' | 'info';
}

export interface PermanentDeleteConfirmationDialog {
  id: string;
  title: string;
  message: string;
  warningText: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  type?: 'danger' | 'warning' | 'info';
}

interface ToastContextType {
  toasts: ToastMessage[];
  confirmations: ConfirmationDialog[];
  deleteErrorDialogs: DeleteErrorDialog[];
  permanentDeleteConfirmations: PermanentDeleteConfirmationDialog[];
  showToast: (message: string, type?: ToastMessage['type'], duration?: number) => void;
  showConfirmation: (dialog: Omit<ConfirmationDialog, 'id'>) => void;
  showDeleteErrorDialog: (dialog: Omit<DeleteErrorDialog, 'id'>) => void;
  showPermanentDeleteConfirmation: (dialog: Omit<PermanentDeleteConfirmationDialog, 'id'>) => void;
  removeToast: (id: string) => void;
  removeConfirmation: (id: string) => void;
  removeDeleteErrorDialog: (id: string) => void;
  removePermanentDeleteConfirmation: (id: string) => void;
  clearAllDialogs: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [confirmations, setConfirmations] = useState<ConfirmationDialog[]>([]);
  const [deleteErrorDialogs, setDeleteErrorDialogs] = useState<DeleteErrorDialog[]>([]);
  const [permanentDeleteConfirmations, setPermanentDeleteConfirmations] = useState<
    PermanentDeleteConfirmationDialog[]
  >([]);

  const showToast = (message: string, type: ToastMessage['type'] = 'info', duration = 5000) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: ToastMessage = { id, message, type, duration };

    console.log('🍞 Mostrando toast:', newToast);
    setToasts((prev) => [...prev, newToast]);

    if (duration > 0) {
      // eslint-disable-next-line no-undef
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  };

  const showConfirmation = (dialog: Omit<ConfirmationDialog, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newConfirmation: ConfirmationDialog = { ...dialog, id };

    console.log('🔔 Mostrando confirmación:', newConfirmation);
    setConfirmations((prev) => [...prev, newConfirmation]);
  };

  const showDeleteErrorDialog = (dialog: Omit<DeleteErrorDialog, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newDeleteErrorDialog: DeleteErrorDialog = { ...dialog, id };

    console.log('🚫 Mostrando diálogo de error de eliminación:', newDeleteErrorDialog);
    setDeleteErrorDialogs((prev) => [...prev, newDeleteErrorDialog]);
  };

  const showPermanentDeleteConfirmation = (
    dialog: Omit<PermanentDeleteConfirmationDialog, 'id'>
  ) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newPermanentDeleteConfirmation: PermanentDeleteConfirmationDialog = { ...dialog, id };

    console.log(
      '🗑️ Mostrando confirmación de eliminación permanente:',
      newPermanentDeleteConfirmation
    );
    setPermanentDeleteConfirmations((prev) => [...prev, newPermanentDeleteConfirmation]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const removeConfirmation = (id: string) => {
    setConfirmations((prev) => prev.filter((confirmation) => confirmation.id !== id));
  };

  const removeDeleteErrorDialog = (id: string) => {
    setDeleteErrorDialogs((prev) => prev.filter((dialog) => dialog.id !== id));
  };

  const removePermanentDeleteConfirmation = (id: string) => {
    setPermanentDeleteConfirmations((prev) => prev.filter((dialog) => dialog.id !== id));
  };

  const clearAllDialogs = () => {
    setToasts([]);
    setConfirmations([]);
    setDeleteErrorDialogs([]);
    setPermanentDeleteConfirmations([]);
    console.log('🧹 Limpiando todos los diálogos');
  };

  const value: ToastContextType = {
    toasts,
    confirmations,
    deleteErrorDialogs,
    permanentDeleteConfirmations,
    showToast,
    showConfirmation,
    showDeleteErrorDialog,
    showPermanentDeleteConfirmation,
    removeToast,
    removeConfirmation,
    removeDeleteErrorDialog,
    removePermanentDeleteConfirmation,
    clearAllDialogs,
  };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};
